/**
 * @file useOfflineRecharge.tsx
 * @description Hook principal para o fluxo de recarga offline V2.
 *
 * Gerencia todo o ciclo de vida da recarga offline em cartões cashless:
 * - Leitura do cartão via leitor NFC
 * - Validação de cartão (saldo, associação, setor)
 * - Abertura de conta (cartão fechado) ou recarga (cartão aberto)
 * - Pagamento (PIX, débito, crédito)
 * - Gravação do saldo no cartão
 * - Sincronização com servidor
 * - Impressão de comprovante
 *
 * Fluxo típico:
 * 1. WAITING_CARD_READ → leitura automática do cartão
 * 2. INSERT_RECHARGE_VALUE → usuário informa valor (ou WAITING_CUSTOMER_DATA se conta fechada)
 * 3. PROCESSING_PAYMENT → pagamento via OrderProvider
 * 4. WRITING → gravação no cartão (até 3 tentativas)
 * 5. Sucesso → impressão, sync e reset
 *
 * @module useOfflineRecharge
 */

import { useCallback, useEffect, useRef, useState } from "react";
import React from "react";
import { useTranslation } from "i18n/useTranslation";
import { useHistory } from "react-router-dom";
import { useCatalogStore } from "application/contexts/catalog/CatalogStore";
import { useSession } from "application/contexts/session/SessionContext";
import { useUi } from "application/contexts/ui/UIContext";
import { useIdle } from "application/contexts/idle/IdleContext";
import { useShallow } from "zustand/react/shallow";
import { hideCnpj, hideCpf, validateCNPJ, validateCPF } from "utils/utils";
import { IFullCardData } from "domains/cashless/aggregates/ICardCashless";
import { Order } from "domains/order/agreggates/order/Order";
import { LocalStorageCashlessOfflineRepository } from "Infrastructure/repositories/localStorage/LocalStorageCashlessOfflineRepository";
import { MeepOrderRepository } from "Infrastructure/repositories/api/OrderRepository";
import { PedidoPos, ProdutoTipo } from "Infrastructure/interfaces/PedidoMeep";
import {
  readCard,
  writeOfflineBalance,
  writeOfflineCard,
} from "modules/reactNativeBridge/ReactNativeBridge";
import { IOfflineCardData } from "modules/cardReader/domain/models/IOfflineCardData";
import { CreatePrintOfflineRechargeUseCase } from "modules/order/application/useCases/CreatePrintOfflineRechargeUseCase";
import { usePrinterStore } from "application/contexts/printer/PrinterStore";
import { PrinterStore } from "application/contexts/printer/PrinterStore";
import { ICustomer } from "modules/order/domain/models/ICustomer";
import { v4 } from "uuid";
import type {
  UseOfflineRechargeV2Params,
  IUseOfflineRecharge,
  IOfflineCardState,
  IPaymentCashlessOfflineParams,
  IPaymentCashlessOfflineResult,
  IOnSubmitRechargeValueParams,
  IOnValidateRechargeValue,
  IWriteRechargeToCardAfterPaymentParams,
} from "./IUseOffilineRecharge.type";
import { OfflineRechargeStep } from "./IUseOffilineRecharge.type";
import { CashlessRepository } from "Infrastructure/repositories/api/CashlessRepository";

/** Mensagem de erro exibida quando o cartão não possui saldo ou associação válida. */
const INVALID_OFFLINE_CARD_ERROR =
  "Cartão sem saldo ou sem associação. Leitura inválida.";

/** Repositório local para pedidos de recarga offline (persistência e sincronização). */
const cashlessOfflineRepository = LocalStorageCashlessOfflineRepository();
/** Repositório de pedidos para sincronização com a API. */
const orderRepository = MeepOrderRepository();

/**
 * Verifica se os dados do cartão offline são válidos para recarga.
 * Cartão válido deve ter saldo numérico e AssociationId.
 *
 * @param data - Dados brutos retornados pela leitura do cartão
 * @returns Type guard: true se data é IOfflineCardData válido
 */
function isValidOfflineCardData(
  data: IOfflineCardData | null | undefined
): data is IOfflineCardData {
  if (!data) return false;
  const hasBalance = typeof data.Balance === "number";
  const hasAssociationId = !!data.AssociationId;
  return hasBalance && hasAssociationId;
}

/**
 * Converte dados brutos do cartão (IOfflineCardData) para o formato completo (IFullCardData).
 * Extrai documento e código do país quando o campo Document vem no formato "doc#countryCode".
 *
 * @param data - Dados brutos do cartão lido
 * @returns Dados do cartão no formato esperado pela aplicação
 */
function mapToFullCardData(data: IOfflineCardData): IFullCardData {
  const [doc, countryCode] = (data.Document ?? "").split("#");
  return {
    Tag: data.Tag,
    Document: doc ?? data.Document,
    CountryCode: countryCode,
    Balance: data.Balance,
    SectionKey: data.SectionKey,
    AssociationId: data.AssociationId,
  };
}

/**
 * Hook principal para o fluxo de recarga offline V2.
 *
 * Controla o estado e as ações do fluxo completo: leitura do cartão, validação,
 * pagamento, gravação no cartão e sincronização. Suporta tanto recarga em conta
 * já aberta quanto abertura de conta (cartão fechado).
 *
 * @param params - Configuração do hook
 * @param params.kiosk - Dados do quiosque (localId, cashlessCode, etc.)
 * @param params.orderProvider - Ref do OrderProvider para iniciar pagamentos
 * @param params.openCustomerForClosedCard - Callback para abrir modal de cliente quando cartão fechado
 * @returns Objeto com estado e handlers para a UI de recarga offline
 *
 * @example
 * ```tsx
 * const { step, offlineCard, onSubmitRechargeValue, ... } = useOfflineRecharge({
 *   kiosk,
 *   orderProvider,
 *   openCustomerForClosedCard,
 * });
 * ```
 */
export function useOfflineRecharge(
  params: UseOfflineRechargeV2Params,
): IUseOfflineRecharge {
  const { kiosk, orderProvider, closeCustomerModal } = params;
  const history = useHistory();
  const { t } = useTranslation();
  const { toast, toastFullscreen } = useUi();
  const { getConfigurationProduct } = useCatalogStore();
  const { sessaoIdPos } = useSession();
  const { handleActiveDefaultIdleTimer, handleDisableIdleTimer } = useIdle();
  const [, setReadingOfflineCard] = useState(false);
  const [addOrdersToPrintQueue] = usePrinterStore(
    useShallow((state: PrinterStore) => [state.addOrders])
  );

  /* --- Estado do fluxo --- */
  const [step, setStep] = useState<OfflineRechargeStep>(OfflineRechargeStep.WAITING_CARD_READ);
  const [offlineCard, setOfflineCard] = useState<IOfflineCardState | null>(null);
  const [rechargeValueCents, setRechargeValueCents] = useState(0);
  const [customer, setCustomer] = useState<ICustomer | null>(null);
  const [showRetryReadButton, setShowRetryReadButton] = useState(false);

  /* --- Refs para controle de efeitos --- */
  const hasStartedReadRef = useRef(false);
  const isMountedRef = useRef(true);
  const abortingDueToPendingRechargeRef = useRef(false);
  const offlineCardRef = useRef<IOfflineCardState | null>(null);

  useEffect(() => {
    offlineCardRef.current = offlineCard;
  }, [offlineCard]);

  useEffect(() => {
    const blockingSteps = [
      OfflineRechargeStep.PROCESSING_PAYMENT,
      OfflineRechargeStep.WRITING,
      OfflineRechargeStep.WAITING_CARD_WRITE,
      OfflineRechargeStep.PENDING_RECHARGE,
      OfflineRechargeStep.PENDING_OPEN_ACCOUNT,
    ];
    if (blockingSteps.includes(step)) {
      handleDisableIdleTimer();
    } else {
      handleActiveDefaultIdleTimer();
    }
  }, [step, handleActiveDefaultIdleTimer, handleDisableIdleTimer]);

  const orderProviderRef = orderProvider;

  /**
   * Lê o cartão no leitor NFC e retorna os dados offline.
   *
   * @param onlyUid - Se true, lê apenas a UID do cartão. Padrão: false
   * @param disableBeep - Se true, desativa o beep do leitor. Padrão: false
   * @returns Dados do cartão (Tag, Document, Balance em centavos, AssociationId, SectionKey) ou null em falha
   * @throws Erro quando cartão inválido (sem saldo ou sem AssociationId)
   */
  const readCashlessOfflineCardData = useCallback(
    async (
      onlyUid = false,
      disableBeep = false
    ): Promise<IOfflineCardState | null> => {
      setReadingOfflineCard(true);
      try {
        const result = await readCard(onlyUid, disableBeep);
        if (!result) {
          return null;
        }
        if (!result.success) {
          throw new Error(result.message ?? "Falha na leitura do cartão");
        }
        const offlineData = result.offlineCardData ?? null;
        const isClosed = result.isClosed ?? false;
        const uid = result.uid ?? "";
        if (offlineData) {
          if (!isValidOfflineCardData(offlineData)) {
            throw new Error(INVALID_OFFLINE_CARD_ERROR);
          }
          const state: IOfflineCardState = {
            cardData: mapToFullCardData(offlineData),
            isClosed,
            uid,
          };
          setOfflineCard(state);
          return state;
        }
        const state: IOfflineCardState = { isClosed, uid };
        setOfflineCard(state);
        return state;
      } finally {
        setReadingOfflineCard(false);
      }
    },
    []
  );

  /**
   * Normaliza erros de leitura/gravação para mensagens amigáveis ao usuário.
   * Mapeia códigos técnicos para chaves i18n (read_error_title, write_failed_title, etc.).
   *
   * @param error - Erro capturado (Error ou string)
   * @returns Mensagem traduzida para exibição
   */
  const onError = useCallback(
    (error: unknown): string => {
      if (error instanceof Error) {
        const msg = error.message;
        if (msg === INVALID_OFFLINE_CARD_ERROR) {
          return t("offline_recharge.messages.read_error_title") ?? msg;
        }
        if (msg.includes("Err intr")) {
          const parts = msg.split(":");
          if (parts[0] === "Err intr") {
            return (
              t("offline_recharge.messages.read_error_title", {
                reason: parts[1]?.trim() ?? "",
              }) ?? msg
            );
          }
        }
        if (
          msg.includes("Falha na leitura") ||
          msg.includes("Falha ao gravar") ||
          msg.includes("gravar recarga")
        ) {
          return t("offline_recharge.messages.write_failed_title") ?? msg;
        }
        if (msg.includes("record_mismatch") || msg.includes("Cartão incorreto")) {
          return t("offline_recharge.messages.record_mismatch") ?? msg;
        }
        if (msg.includes("section_key") || msg.includes("setor")) {
          return t("offline_recharge.messages.section_key_mismatch") ?? msg;
        }
        if (msg.includes("function_unavailable") || msg.includes("desatualizado")) {
          return t("offline_recharge.messages.function_unavailable") ?? msg;
        }
        return msg;
      }
      if (typeof error === "string") return error;
      return t("offline_recharge.messages.read_failed") ?? "Erro desconhecido";
    },
    [t]
  );

  /**
   * Exibe toast de erro fullscreen com título opcional, mensagem e rodapé.
   * Usado para erros de leitura, gravação e tentativas de retry.
   */
  const showErrorToast = useCallback(
    (params: {
      message: React.ReactNode;
      titleKey?: string;
      footer?: React.ReactNode | null;
      onClose?: () => void;
      time?: number;
    }) => {
      const { message, titleKey, footer, onClose, time = 5000 } = params;
      toastFullscreen(
        <>
          {titleKey && <><strong>{t(titleKey)}</strong><br /><br /></>}
          {message}
          {footer != null && <><br /><br />{footer}</>}
        </>,
        "error",
        { time, onClose }
      );
    },
    [t, toastFullscreen]
  );

  /**
   * Grava todos os dados no cartão (abertura de conta / cartão fechado).
   * Usado após pagamento de abertura: grava apenas o valor da recarga (ativação cobrada não vai no cartão).
   *
   * @param data - Dados completos do cartão incluindo Balance em centavos
   * @returns Resultado da gravação (success, message)
   */
  const writeOfflineCardData = useCallback(
    async (
      data: IFullCardData & { Balance: number }
    ): Promise<{ success: boolean; message: string }> => {
      const payload: IOfflineCardData = {
        Tag: data.Tag,
        Document: data.Document,
        CountryCode: data.CountryCode,
        Balance: data.Balance,
        AssociationId: data.AssociationId,
        SectionKey: data.SectionKey,
      };
      return writeOfflineCard(payload);
    },
    []
  );

  /**
   * Grava apenas o saldo no cartão (recarga em conta já aberta).
   * Após pagamento, soma o valor ao saldo atual e grava o novo total.
   *
   * @param tag - UID/tag do cartão
   * @param balanceCents - Novo saldo total em centavos (não o incremento)
   */
  const writeBalance = useCallback(
    async (tag: string, balanceCents: number): Promise<void> => {
      await writeOfflineBalance(tag, balanceCents);
    },
    []
  );

  /**
   * Sincroniza pedidos de recarga offline com o servidor.
   *
   * @returns Lista de respostas da sincronização
   */
  const runSyncOrdersAsync = useCallback(async () => {
    const localId = kiosk?.localId ?? "";
    const equipamentId = kiosk?.id ?? "";
    return orderRepository.syncOrdersAsync(localId, equipamentId);
  }, [kiosk]);

  /**
   * Envia o comprovante de recarga para a fila de impressão.
   *
   * @param params - Dados da recarga (saldo anterior, novo saldo, pedido, tag, etc.)
   */
  const printCardDataRecharge = useCallback(
    (params: {
      newBalance: number;
      previousBalance: number;
      orderId: string;
      tag: string;
      createdAt: string;
      document?: string;
      name?: string;
    }) => {
      const ordersToPrint = CreatePrintOfflineRechargeUseCase(
        params.newBalance,
        params.previousBalance,
        params.orderId,
        params.tag,
        params.createdAt,
        params.document,
        params.name
      );
      addOrdersToPrintQueue(ordersToPrint);
    },
    [addOrdersToPrintQueue]
  );

  /**
   * Busca recargas (cartão já aberto) pagas mas não sincronizadas nem gravadas no cartão.
   *
   * @param associationId - ID da associação do cartão
   * @returns Lista de pedidos pendentes ou null
   */
  const hasPendingRechargeNotSynchronized = useCallback(async (associationId: string): Promise<PedidoPos[] | null> => {
    const list = await cashlessOfflineRepository.getNotSincronizedOfflineRechargesAndNotRecordedByAssociationId(associationId);
    return list ?? null;
  }, []);

  const updateAllPendingRechargeAndOpenAccount = useCallback(async (ordersId: string[]): Promise<void> => {
    if (ordersId && ordersId.length > 0) {
      ordersId.forEach(async (orderId) => {
        const ped = await cashlessOfflineRepository.getPedidoPosById(orderId);
        if (ped) {
          ///Se o pedido está no repositório local, atualiza o status de gravação
          ped.isOfflineRechargeRecordConfirmed = true;
          await cashlessOfflineRepository.savePendentConfirmationRecordOfflineOrders([ped]);
        } else {
          ///Se o pedido não está no repositório local, adiciona o pedido ao repositório local
          await cashlessOfflineRepository.addConfirmationRecordOfflineOrders([orderId]);
        }
      });
    }
  }, []);

  /**
   * Marca a recarga como gravada no cartão no repositório local.
   * Chamado após writeBalance ou writeOfflineCardData com sucesso.
   *
   * @param orderId - ID do pedido de recarga
   */
  const confirmRechargeLocally = useCallback(async (orderId: string): Promise<void> => {
    await cashlessOfflineRepository.markAsRecorded(orderId);
    //await runSyncOrdersAsync();
  }, []);

  /** Marca abertura de conta como gravada no cartão (local). Após writeOfflineCardData com sucesso. */
  //const confirmOpenAccountLocally = useCallback(async (orderId: string): Promise<void> => {
  //  await cashlessOfflineRepository.markAsRecorded(orderId);
  //  await runSyncOrdersAsync();
  //}, [runSyncOrdersAsync]);

  /**
   * Cria o pedido de recarga offline para envio ao fluxo de pagamento.
   * Inclui item de recarga e, se conta fechada, valor de ativação.
   *
   * @param valueReais - Valor da recarga em reais
   * @param cardDataOffline - Dados do cartão lido
   * @param isClosed - Se true, adiciona valor de ativação ao total
   * @returns Order pronto para pagamento ou null se produto de recarga não configurado
   */
  const createOfflineRechargeOrder = useCallback(
    (valueReais: number, cardDataOffline: IFullCardData, isClosed?: boolean): Order | null => {
      const rechargeProduct = getConfigurationProduct(ProdutoTipo.Recarga);
      if (!rechargeProduct) {
        toastFullscreen(t("offline_recharge.messages.config_products_missing"), "error");
        return null;
      }
      const activationProduct = getConfigurationProduct(ProdutoTipo.AtivacaoCashless);
      const newOrderId = v4();
      const newOrder: Order = {
        sectionKey: cardDataOffline.SectionKey,
        isClosed: isClosed ?? false,
        activationValue: isClosed
          ? (activationProduct?.price ?? activationProduct?.realPrice ?? 0)
          : 0,
        isOfflineRecharge: true,
        isDataRecorded: false,
        isOfflineRechargeRecordConfirmed: false,
        cartDate: new Date().toISOString(),
        id: newOrderId,
        sessionId: sessaoIdPos ?? undefined,
        orderItems: [
          {
            id: v4(),
            name: rechargeProduct?.name ?? "",
            category: "",
            price: valueReais,
            realPrice: valueReais,
            totalPrice: valueReais,
            description: null,
            productPrice: valueReais,
            productId: rechargeProduct?.id ?? "",
            productType: ProdutoTipo.Recarga,
            quantity: 1,
            imageUrl: "",
            thumbnailUrl: "",
            hideCatalog: false,
            barcode: null,
            orderComposition: null,
            compositions: null,
            printerName: null,
            translations: [],
          },
        ],
        totalValue:
          valueReais +
          (isClosed ? activationProduct?.price ?? activationProduct?.realPrice ?? 0 : 0),
        friendlyId:
          (kiosk?.kioskName?.replace(/\D/gm, "") ?? "") + " " + newOrderId.substring(0, 4),
        createdAt: new Date().toISOString(),
        customer: {
          orderId: newOrderId,
          tag: cardDataOffline.Tag,
          number: cardDataOffline.Tag,
          orderPad: cardDataOffline.Tag,
          document: cardDataOffline.Document,
          balance: cardDataOffline.Balance,
          associationId: cardDataOffline.AssociationId,
        },
      };
      return newOrder;
    },
    [getConfigurationProduct, kiosk, sessaoIdPos, t, toastFullscreen]
  );

  /**
   * Inicia o pagamento da recarga offline via OrderProvider.
   * Cria o pedido, chama startPayment e retorna o resultado.
   *
   * @param params - Valor, dados do cartão, tipo de transação
   * @returns Resultado com order e payment em caso de sucesso, null em falha
   */
  const paymentCashlessOffline = useCallback(
    async (
      params: IPaymentCashlessOfflineParams
    ): Promise<IPaymentCashlessOfflineResult | null> => {
      const { rechargeValueCents, cardData, isClosed, transactionType } = params;
      if (rechargeValueCents <= 0 || !cardData) {
        toast(t("offline_recharge.messages.invalid_value"), "warning");
        return null;
      }
      const valueReais = rechargeValueCents / 100;
      const newOrder = createOfflineRechargeOrder(valueReais, cardData, isClosed);
      if (!orderProviderRef.current || !newOrder) return null;
      try {
        const result = await orderProviderRef.current.startPayment(newOrder, "Recharge", transactionType);
        if (result?.order && result?.payment && result.payment.success) {
          return result;
        }
        return null;
      } catch (error) {
        return null;
      }
    },
    [createOfflineRechargeOrder, orderProviderRef, t, toast]
  );

  /**
   * Grava a recarga no cartão após pagamento aprovado.
   * - Conta fechada: grava apenas o valor da recarga (ativação cobrada não vai no cartão)
   * - Conta aberta: soma a recarga ao saldo atual e chama writeBalance
   *
   * @param params - Pedido pago, dados do cartão e flag isClosed
   */
  const writeRechargeToCardAfterPayment = useCallback(
    async (params: IWriteRechargeToCardAfterPaymentParams): Promise<void> => {
      const { order, cardData, isClosed } = params;
      console.log("[writeRechargeToCardAfterPayment] order", order);
      console.log("[writeRechargeToCardAfterPayment] cardData", cardData);
      console.log("[writeRechargeToCardAfterPayment] isClosed", isClosed);
      if (isClosed) {
        const rechargeValueCents = Math.round(
          (order.totalValue - (order.activationValue ?? 0)) * 100
        );
        await writeOfflineCardData({
          ...cardData,
          Balance: rechargeValueCents,
        });
      } else {
        const rechargeValueCents = Math.round(order.totalValue * 100);
        const newBalanceCents = cardData.Balance + rechargeValueCents;
        await writeBalance(cardData.Tag, newBalanceCents);
      }
    },
    [writeOfflineCardData, writeBalance]
  );

  /**
   * Reseta todo o fluxo de recarga para o estado inicial.
   * Limpa step, cartão, valor, cliente e refs.
   */
  const resetFlow = useCallback(() => {
    setShowRetryReadButton(false);
    hasStartedReadRef.current = false;
    setStep(OfflineRechargeStep.WAITING_CARD_READ);
    setOfflineCard(null);
    setRechargeValueCents(0);
    setCustomer(null);
  }, []);

  interface IPendingOfflineRechargeAndOpenAccountParams {
    tag: string;
    document: string;
    associationId: string;
    balance: number;
    sectionKey?: string;
    isClosed: boolean;
    orderId?: string;
    rechargeValue: number;
    dataPedido: Date;
  }

  ///**
  // * Grava uma recarga pendente (já paga) no cartão.
  // * Até 3 tentativas de escrita; em sucesso: confirma localmente, imprime e sincroniza.
  // *
  // * @param pedidoPos - Pedido de recarga pendente
  // * @param cardData - Dados atuais do cartão
  // */
  //const writePendingRechargeToCard = useCallback(
  //  async (pedidoPos: PedidoPos, cardData: IFullCardData, isClosed?: boolean): Promise<void> => {
  //    const maxAttempts = 3;
  //    if (!pedidoPos.pedidoPOSId) {
  //      toastFullscreen(t("offline_recharge.messages.write_failed_title"), "error");
  //      history.replace("/");
  //      resetFlow();
  //      return;
  //    }

  //    let rechargeValueCents = Math.round(pedidoPos.valorTotal * 100);
  //    let newBalanceCents = (cardData.Balance?? 0) + rechargeValueCents;
  //    let writeSuccess = false;

  //    for (let attempt = 0; attempt < maxAttempts && !writeSuccess; attempt++) {
  //      try {
  //        if (pedidoPos.isClosed) {
  //          const data = {
  //            Tag: pedidoPos.tagCashless,
  //            Document: pedidoPos.clienteEstabelecimentoObject.cpf ?? pedidoPos.tagCashless,
  //            AssociationId: pedidoPos.clienteEstabelecimentoObject.pedidoPosPaiId ?? "",
  //            Balance: newBalanceCents,
  //            SectionKey: pedidoPos.sectionKey ?? "",
  //          }
  //          await writeOfflineCardData(data);
  //        } else {
  //          await writeBalance(cardData.Tag, newBalanceCents);
  //        }
  //        writeSuccess = true;
  //        await confirmRechargeLocally(pedidoPos.pedidoPOSId!);
  //        printCardDataRecharge({
  //          newBalance: newBalanceCents / 100,
  //          previousBalance: cardData.Balance / 100,
  //          orderId: pedidoPos.codigoPedido,
  //          tag: cardData.Tag,
  //          createdAt: pedidoPos.dataPedido != null
  //            ? (pedidoPos.dataPedido instanceof Date
  //              ? pedidoPos.dataPedido
  //              : new Date(pedidoPos.dataPedido as string)
  //            ).toISOString()
  //            : new Date().toISOString(),
  //          document: cardData.Document,
  //          name: customer?.name,
  //        });
  //        toastFullscreen(t("offline_recharge.messages.write_success_title"), "success");
  //        runSyncOrdersAsync();
  //        resetFlow();
  //      } catch (error) {
  //        console.error("Erro ao escrever recarga pendente no cartão", error);
  //        const isLastAttempt = attempt === maxAttempts - 1;
  //        const message = error instanceof Error ? error.message : t("offline_recharge.messages.write_failed_title");
  //        showErrorToast({
  //          message,
  //          footer: isLastAttempt
  //            ? t("offline_recharge_v2.write_failed_after_attempts")
  //            : process.env.NODE_ENV === "development"
  //              ? t("offline_recharge_v2.retry_attempt", { current: attempt + 1, total: maxAttempts })
  //              : null,
  //          onClose: () => {
  //            if (isLastAttempt) {
  //              history.replace("/");
  //              resetFlow();
  //            }
  //          },
  //        });
  //        if (isLastAttempt) {
  //          runSyncOrdersAsync();
  //          history.replace("/");
  //          resetFlow();
  //          return;
  //        }
  //      }
  //    }
  //  },
  //  [toastFullscreen, t, history, resetFlow, confirmRechargeLocally, printCardDataRecharge, customer, runSyncOrdersAsync, writeOfflineCardData, writeBalance, showErrorToast]
  //);






  /**
   * Grava uma recarga pendente (já paga) no cartão.
   * Até 3 tentativas de escrita; em sucesso: confirma localmente, imprime e sincroniza.
   *
   * @param pedidoPos - Pedido de recarga pendente
   * @param cardData - Dados atuais do cartão
   */
  const writePendingOfflineRechargeAndOpenAccount = useCallback(
    async (data: IPendingOfflineRechargeAndOpenAccountParams): Promise<void> => {
      const maxAttempts = 3;
      console.log("[writePendingOfflineRechargeAndOpenAccount] data", data);
      let rechargeValueCents = Math.round(data.rechargeValue);
      let newBalanceCents = (data.balance ?? 0) + rechargeValueCents;
      let writeSuccess = false;

      console.log("[writePendingOfflineRechargeAndOpenAccount] rechargeValueCents", rechargeValueCents);
      console.log("[writePendingOfflineRechargeAndOpenAccount] newBalanceCents", newBalanceCents);

      for (let attempt = 0; attempt < maxAttempts && !writeSuccess; attempt++) {
        try {
          if (data.isClosed) {
            const offlineCardData: IFullCardData = {
              Tag: data.tag,
              Document: data.document,
              AssociationId: data.associationId,
              Balance: newBalanceCents,
              SectionKey: data.sectionKey ?? "",
            }
            console.log("[writePendingOfflineRechargeAndOpenAccount] offlineCardData", offlineCardData);
            await writeOfflineCardData(offlineCardData);
          } else {
            console.log("[writePendingOfflineRechargeAndOpenAccount] writeBalance", data.tag, newBalanceCents);
            await writeBalance(data.tag, newBalanceCents);
          }
          writeSuccess = true;
          if (data.orderId) {
            await confirmRechargeLocally(data.orderId);
            runSyncOrdersAsync();
          }
          printCardDataRecharge({
            newBalance: newBalanceCents / 100,
            previousBalance: data.balance / 100,
            orderId: data.orderId ?? "RECARGA",
            tag: data.tag,
            createdAt: data.dataPedido != null
              ? (data.dataPedido instanceof Date
                ? data.dataPedido
                : new Date(data.dataPedido as string)
              ).toISOString()
              : new Date().toISOString(),
            document: data.document,
            name: customer?.name,
          });
          toastFullscreen(t("offline_recharge.messages.write_success_title"), "success");
          resetFlow();
        } catch (error) {
          console.error("Erro ao escrever recarga pendente no cartão", error);
          const isLastAttempt = attempt === maxAttempts - 1;
          const message = error instanceof Error ? error.message : t("offline_recharge.messages.write_failed_title");
          showErrorToast({
            message,
            footer: isLastAttempt
              ? t("offline_recharge_v2.write_failed_after_attempts")
              : process.env.NODE_ENV === "development"
                ? t("offline_recharge_v2.retry_attempt", { current: attempt + 1, total: maxAttempts })
                : null,
            onClose: () => {
              if (isLastAttempt) {
                history.replace("/");
                resetFlow();
              }
            },
          });
          if (isLastAttempt) {
            runSyncOrdersAsync();
            history.replace("/");
            resetFlow();
            return;
          }
        }
      }
    },
    [toastFullscreen, t, history, resetFlow, confirmRechargeLocally, printCardDataRecharge, customer, runSyncOrdersAsync, writeOfflineCardData, writeBalance, showErrorToast]
  );





  /**
   * Handler principal do submit do valor de recarga.
   * Valida valor e cartão, processa pagamento, grava no cartão (até 3 tentativas) e imprime.
   *
   * @param params - Valor em centavos, dados do cartão, isClosed, transactionType
   * @returns Resultado da validação (valid, errorMessage)
   */
  const onSubmitRechargeValue = useCallback(
    async (params: IOnSubmitRechargeValueParams): Promise<IOnValidateRechargeValue> => {
      console.log("[onSubmitRechargeValue] params", params);
      const { rechargeValueCents: valueCents, cardData, isClosed, transactionType } = params;
      if (valueCents <= 0) {
        toast(t("offline_recharge.messages.invalid_value"), "warning");
        return { valid: false, errorMessage: t("offline_recharge.messages.invalid_value") };
      }
      if (!cardData) {
        const msg = t("offline_recharge.messages.read_failed");
        toast(msg, "warning");
        return { valid: false, errorMessage: msg };
      }
      // Bloqueia abertura de conta quando ativação está desabilitada no backend
      if (isClosed && kiosk?.enableCashlessActivation === false) {
        const msg = t("offline_recharge.messages.activation_disabled");
        toastFullscreen(msg, "error");
        return { valid: false, errorMessage: msg };
      }
      if (
        !isClosed &&
        kiosk?.cashlessCode != null &&
        !cardData.SectionKey?.includes(kiosk.cashlessCode)
      ) {
        const msg = t("offline_recharge.messages.section_key_mismatch");
        toastFullscreen(msg, "warning", { time: 4000 });
        return { valid: false, errorMessage: msg };
      }

      setStep(OfflineRechargeStep.PROCESSING_PAYMENT);
      const cardDataWithDocument = {
        ...cardData,
        Document: cardData.Document ?? params.uid,
      }
      try {
        const result = await paymentCashlessOffline({
          rechargeValueCents: valueCents,
          cardData: cardDataWithDocument,
          isClosed,
          transactionType,
        });
        if (!result?.order) {
          //toast(t("offline_recharge.messages.payment_failed"), "error");
          resetFlow();
          //APENAS RETORNA FALSE PARA QUE O FLUXO CONTINUE POIS O TOAST JA FOI EXIBIDO NO START PAYMENT
          return { valid: false };
        }
        const order = result.order;
        setStep(OfflineRechargeStep.WRITING);

        const maxAttempts = 3;
        let writeSuccess = false;

        for (let attempt = 0; attempt < maxAttempts && !writeSuccess; attempt++) {
          try {
            await writeRechargeToCardAfterPayment({ order, cardData, isClosed });
            writeSuccess = true;
            await confirmRechargeLocally(order.id);
          } catch (err) {
            const isLastAttempt = attempt === maxAttempts - 1;
            const errorMessage = onError(err);
            showErrorToast({
              message: errorMessage,
              titleKey: "offline_recharge_v2.error_keep_card_close",
              footer: isLastAttempt
                ? t("offline_recharge_v2.write_failed_after_attempts")
                : process.env.NODE_ENV === "development"
                  ? t("offline_recharge_v2.retry_attempt", { current: attempt + 1, total: maxAttempts })
                  : null,
              onClose: () => {
                if (isLastAttempt) {
                  history.replace("/");
                  resetFlow();
                }
              },
            });
            if (isLastAttempt) {
              history.replace("/");
              resetFlow();
              return { valid: true };
            }
          }
        }

        if (!writeSuccess) return { valid: true };
        const orderId = order.id;

        const previousBalanceReais = cardData.Balance / 100;
        const rechargeReais = order.totalValue - (order.activationValue ?? 0);
        const newBalanceReais = isClosed
          ? rechargeReais
          : previousBalanceReais + order.totalValue;
        printCardDataRecharge({
          newBalance: newBalanceReais,
          previousBalance: previousBalanceReais,
          orderId,
          tag: cardData.Tag,
          createdAt: order.createdAt ?? new Date().toISOString(),
          document: customer?.document,
          name: customer?.name,
        });
        toastFullscreen(t("offline_recharge_v2.success_message"), "success", {
          time: 3000,
          onClose: () => {
            history.replace("/");
            resetFlow();
          },
        });
        return { valid: true };
      } catch (error) {
        toast(onError(error), "error");
        resetFlow();
        return { valid: false };
      }
    },
    [paymentCashlessOffline, writeRechargeToCardAfterPayment, confirmRechargeLocally, printCardDataRecharge, onError, showErrorToast, toast, toastFullscreen, t, history, resetFlow, kiosk, customer]
  );

  /**
   * Verifica se há recargas ou aberturas de conta pendentes para o cartão lido.
   * Se houver recarga pendente, grava no cartão; se abertura pendente, muda para step PENDING_OPEN_ACCOUNT.
   */
  const verifyPendingLocalRechargeAndOpenAccount = useCallback(async () => {
    const associationId = offlineCard?.cardData?.AssociationId ?? "";
    const tagId = offlineCard?.cardData?.Tag ?? "";
    let pendingRechargePedidoPos: PedidoPos[] | null = null;
    console.log("[verifyPendingLocalRechargeAndOpenAccount] associationId", associationId);
    console.log("[verifyPendingLocalRechargeAndOpenAccount] tagId", tagId);
    if (associationId) {
      pendingRechargePedidoPos = await hasPendingRechargeNotSynchronized(associationId);
      console.log("[verifyPendingLocalRechargeAndOpenAccount] pendingRechargePedidoPos", pendingRechargePedidoPos);
    } else {
      pendingRechargePedidoPos = await hasPendingRechargeNotSynchronized(tagId);
      console.log("[verifyPendingLocalRechargeAndOpenAccount] pendingRechargePedidoPos", pendingRechargePedidoPos);
    }
    console.log("HasLocalPendingRechargeAndOpenAccount", pendingRechargePedidoPos?.length, offlineCard?.cardData, offlineCard?.isClosed);
    if (pendingRechargePedidoPos && pendingRechargePedidoPos.length > 0 && offlineCard?.cardData) {
      abortingDueToPendingRechargeRef.current = true;
      closeCustomerModal?.();
      setStep(OfflineRechargeStep.PENDING_RECHARGE);
      const valorTotalReais = pendingRechargePedidoPos[0].valorTotal ?? 0;
      const rechargeValueCents = Math.round(valorTotalReais * 100);

      if (offlineCard.isClosed) {
        const data: IPendingOfflineRechargeAndOpenAccountParams = {
          tag: pendingRechargePedidoPos[0].tagCashless,
          document: pendingRechargePedidoPos[0].clienteEstabelecimentoObject.cpf ?? pendingRechargePedidoPos[0].tagCashless,
          associationId: pendingRechargePedidoPos[0].clienteEstabelecimentoObject.pedidoPosPaiId ?? "",
          balance: 0,
          sectionKey: pendingRechargePedidoPos[0].sectionKey ?? "",
          isClosed: true,
          orderId: pendingRechargePedidoPos[0].pedidoPOSId ?? "",
          rechargeValue: rechargeValueCents,
          dataPedido: pendingRechargePedidoPos[0].dataPedido ?? new Date(),
        }
        console.log("[verifyPendingLocalRechargeAndOpenAccount] data", data);
        await writePendingOfflineRechargeAndOpenAccount(data);
      } else {
        const data: IPendingOfflineRechargeAndOpenAccountParams = {
          tag: offlineCard.uid,
          document: offlineCard.cardData.Document ?? offlineCard.uid,
          associationId: offlineCard.cardData.AssociationId ?? "",
          balance: offlineCard.cardData.Balance ?? 0,
          sectionKey: offlineCard.cardData.SectionKey ?? "",
          isClosed: false,
          orderId: pendingRechargePedidoPos[0].pedidoPOSId ?? "",
          rechargeValue: rechargeValueCents,
          dataPedido: new Date(),
        }
        console.log("[verifyPendingLocalRechargeAndOpenAccount] data", data);
        await writePendingOfflineRechargeAndOpenAccount(data);
        await updateAllPendingRechargeAndOpenAccount([pendingRechargePedidoPos[0].pedidoPOSId ?? ""]);
        await runSyncOrdersAsync();
      }

      return;
    }
  }, [offlineCard, hasPendingRechargeNotSynchronized, writePendingOfflineRechargeAndOpenAccount, updateAllPendingRechargeAndOpenAccount, runSyncOrdersAsync, closeCustomerModal]);



  const verifyOnlineRechargeAndOpenAccount = useCallback(async (): Promise<void> => {
    const associationId = offlineCard?.cardData?.AssociationId;
    const tagId = offlineCard?.uid ?? "";
    console.log("[verifyOnlineRechargeAndOpenAccount] associationId", associationId);
    console.log("[verifyOnlineRechargeAndOpenAccount] tagId", tagId);
    if (associationId || tagId) {
      const pendingRechargePedidoPos = await CashlessRepository().getPendingRecharge({
        accountId: associationId,
        tag: tagId,
        localId: kiosk?.localId ?? "",
      })
      console.log("[verifyOnlineRechargeAndOpenAccount] pendingRechargePedidoPos", pendingRechargePedidoPos);

      if (pendingRechargePedidoPos && pendingRechargePedidoPos.ordersId && pendingRechargePedidoPos.ordersId.length > 0) {
        abortingDueToPendingRechargeRef.current = true;
        closeCustomerModal?.();
        setStep(OfflineRechargeStep.PENDING_RECHARGE);
        
        console.log("[verifyOnlineRechargeAndOpenAccount] pendingRechargePedidoPos.ordersId", pendingRechargePedidoPos.ordersId);

        const rechargeValue = Math.round(pendingRechargePedidoPos.rechargeAmount * 100);
        const writePendingOfflineRechargeAndOpenAccountParams: IPendingOfflineRechargeAndOpenAccountParams = {
          tag: tagId,
          document: pendingRechargePedidoPos?.document ?? "",
          associationId: pendingRechargePedidoPos?.accountId ?? "",
          balance: offlineCard?.cardData?.Balance ?? 0,
          sectionKey: kiosk?.cashlessCode ?? "",
          isClosed: offlineCard?.isClosed ?? false,
          rechargeValue: rechargeValue,
          dataPedido: new Date(),
        }
        console.log("[verifyOnlineRechargeAndOpenAccount] writePendingOfflineRechargeAndOpenAccountParams", writePendingOfflineRechargeAndOpenAccountParams);
        await writePendingOfflineRechargeAndOpenAccount(writePendingOfflineRechargeAndOpenAccountParams);
        if (pendingRechargePedidoPos.ordersId && pendingRechargePedidoPos.ordersId.length > 0) {
          console.log("[verifyOnlineRechargeAndOpenAccount] updateAllPendingRechargeAndOpenAccount", pendingRechargePedidoPos.ordersId);
          await updateAllPendingRechargeAndOpenAccount(pendingRechargePedidoPos.ordersId);
        }
      }
      await runSyncOrdersAsync();
    }
  }, [kiosk, offlineCard, runSyncOrdersAsync, updateAllPendingRechargeAndOpenAccount, writePendingOfflineRechargeAndOpenAccount, closeCustomerModal]);



  /** Valor de ativação (ProdutoTipo.AtivacaoCashless) para exibição na UI quando conta fechada. */
  const activationDisplayValue =
    getConfigurationProduct(ProdutoTipo.AtivacaoCashless)?.price ??
    getConfigurationProduct(ProdutoTipo.AtivacaoCashless)?.realPrice ??
    0;


  /**
   * Handler do botão voltar. Bloqueia durante WRITING/WAITING_CARD_WRITE.
   * Em WAITING_CUSTOMER_DATA, INSERT_RECHARGE_VALUE ou PROCESSING_PAYMENT, volta para leitura do cartão.
   */
  const handleGoBack = useCallback(() => {
    if (
      step === OfflineRechargeStep.WAITING_CARD_WRITE ||
      step === OfflineRechargeStep.WRITING
    ) {
      return;
    }
    if (step === OfflineRechargeStep.WAITING_CUSTOMER_DATA) {
      setShowRetryReadButton(false);
      hasStartedReadRef.current = false;
      setStep(OfflineRechargeStep.WAITING_CARD_READ);
      setOfflineCard(null);
      setCustomer(null);
      return;
    }
    if (
      step === OfflineRechargeStep.INSERT_RECHARGE_VALUE ||
      step === OfflineRechargeStep.PROCESSING_PAYMENT
    ) {
      setShowRetryReadButton(false);
      hasStartedReadRef.current = false;
      setStep(OfflineRechargeStep.WAITING_CARD_READ);
      setOfflineCard(null);
      setRechargeValueCents(0);
      setCustomer(null);
      return;
    }
    history.replace("/");
    resetFlow();
  }, [step, resetFlow, history]);

  /**
   * Executa a leitura do cartão com até 3 tentativas.
   * Valida section_key do quiosque; em sucesso avança para INSERT_RECHARGE_VALUE.
   */
  const handleReadCard = useCallback(async () => {
    const maxAttempts = 3;
    let lastError: unknown = null;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const result = await readCashlessOfflineCardData();
        if (!isMountedRef.current) return;
        if (!result) {
          lastError = new Error(t("offline_recharge.messages.read_failed"));
          continue;
        }
        if (result.cardData && kiosk?.cashlessCode && !result.cardData?.SectionKey?.includes(kiosk?.cashlessCode)) {
          console.log("section_key_mismatch", result.cardData?.SectionKey, kiosk?.cashlessCode);
          throw new Error(t("offline_recharge.messages.section_key_mismatch"));
        }
        setOfflineCard({
          cardData: result.cardData,
          isClosed: result.isClosed,
          uid: result.uid ?? "",
        });
        if (result.isClosed) {
          setStep(OfflineRechargeStep.WAITING_CUSTOMER_DATA);
        } else {
          setStep(OfflineRechargeStep.INSERT_RECHARGE_VALUE);
        }
        return;
      } catch (error) {
        lastError = error;
        if (!isMountedRef.current) return;
        const isLastAttempt = attempt >= maxAttempts - 1;
        if (isLastAttempt) {
          setShowRetryReadButton(false);
          hasStartedReadRef.current = false;
          showErrorToast({
            message: error instanceof Error ? error.message : t("offline_recharge.messages.read_failed"),
            titleKey: "offline_recharge_v2.error_read_title",
            footer: t("offline_recharge_v2.read_failed_after_attempts"),
            onClose: () => {
              history.replace("/");
              resetFlow();
            },
          });
          history.replace("/");
          resetFlow();
          return;
        }
      }
    }

    if (lastError && isMountedRef.current) {
      setShowRetryReadButton(false);
      hasStartedReadRef.current = false;
      setStep(OfflineRechargeStep.WAITING_CARD_READ);
      setOfflineCard(null);
      showErrorToast({
        message: lastError instanceof Error ? lastError.message : t("offline_recharge.messages.read_failed"),
        titleKey: "offline_recharge_v2.error_read_title",
        footer: t("offline_recharge_v2.read_failed_after_attempts"),
        onClose: () => {
          history.replace("/");
          resetFlow();
        },
      });
      history.replace("/");
      resetFlow();
    }
  }, [readCashlessOfflineCardData, kiosk, t, showErrorToast, history, resetFlow]);

  /* Inicia leitura automática quando em WAITING_CARD_READ (evita múltiplas chamadas via hasStartedReadRef). */
  useEffect(() => {
    if (step !== OfflineRechargeStep.WAITING_CARD_READ) {
      hasStartedReadRef.current = false;
      return;
    }
    if (showRetryReadButton) return;
    if (hasStartedReadRef.current) return;
    hasStartedReadRef.current = true;
    handleReadCard();
  }, [step, showRetryReadButton, handleReadCard]);

  /* Ao chegar em INSERT_RECHARGE_VALUE, WAITING_CUSTOMER_DATA ou WAITING_CARD_WRITE, verifica recargas/aberturas pendentes. */
  useEffect(() => {
    if (step === OfflineRechargeStep.INSERT_RECHARGE_VALUE || step === OfflineRechargeStep.WAITING_CUSTOMER_DATA || step === OfflineRechargeStep.WAITING_CARD_WRITE) {
      const verify = async () => {
        await verifyPendingLocalRechargeAndOpenAccount();
        await verifyOnlineRechargeAndOpenAccount();
      }
      verify();
    }
  }, [step, verifyPendingLocalRechargeAndOpenAccount, verifyOnlineRechargeAndOpenAccount]);

  /**
   * Confirma o CPF na etapa WAITING_CUSTOMER_DATA e avança para INSERT_RECHARGE_VALUE.
   */
  const onSubmitCpf = useCallback((document: string | null) => {
    const card = offlineCardRef.current;
    if (!card || !kiosk) return;
    setCustomer({ document: document ?? undefined });
    setOfflineCard({
      cardData: {
        Tag: card.uid,
        Document: document && document.length > 0 ? document : card.uid,
        Balance: 0,
        AssociationId: card.cardData?.AssociationId ?? v4(),
        SectionKey: kiosk.cashlessCode,
      },
      uid: card.uid,
      isClosed: card.isClosed,
    });
    setStep(OfflineRechargeStep.INSERT_RECHARGE_VALUE);
  }, [kiosk]);

  /** Adiciona valor rápido (ex.: botões R$ 10, R$ 20) ao valor da recarga em centavos. */
  const addQuickValue = useCallback((reais: number) => {
    setRechargeValueCents((prev) => prev + reais * 100);
  }, []);

  /** Handler do teclado numérico: dígitos 0-9 ou backspace para editar o valor. */
  const handleKeyPress = useCallback((key: string) => {
    if (key === "backspace") {
      setRechargeValueCents((prev) => Math.floor(prev / 10));
      return;
    }
    const num = parseInt(key, 10);
    if (!Number.isNaN(num) && num >= 0 && num <= 9) {
      setRechargeValueCents((prev) => Math.min(prev * 10 + num, 9999999));
    }
  }, []);

  /** Limpa o valor da recarga (zera centavos). */
  const onClearValue = useCallback(() => setRechargeValueCents(0), []);

  /** Tenta novamente a leitura do cartão. */
  const onRetryRead = useCallback(() => {
    setShowRetryReadButton(false);
    hasStartedReadRef.current = false;
    handleReadCard();
  }, [handleReadCard]);

  /** Índice do step atual para o indicador de progresso (0, 1 ou 2). */
  const currentStepIndex =
    {
      [OfflineRechargeStep.WAITING_CARD_READ]: 0,
      [OfflineRechargeStep.WAITING_CUSTOMER_DATA]: 0,
      [OfflineRechargeStep.INSERT_RECHARGE_VALUE]: 1,
      [OfflineRechargeStep.PROCESSING_PAYMENT]: 1,
      [OfflineRechargeStep.WAITING_CARD_WRITE]: 2,
      [OfflineRechargeStep.WRITING]: 2,
      [OfflineRechargeStep.ERROR]: 0,
      [OfflineRechargeStep.PENDING_RECHARGE]: 0,
      [OfflineRechargeStep.PENDING_OPEN_ACCOUNT]: 0,
    }[step] ?? 0;



  /** Formata o documento (CPF/CNPJ) para exibição com mascaramento (oculta parte dos dígitos). */
  const getDocumentDisplay = useCallback(
    (data: IFullCardData) => {
      const document = data?.Document;
      if (validateCPF(document ?? "")) {
        return `CPF: ${hideCpf(document ?? "")}`;
      }
      if (validateCNPJ(document ?? "")) {
        return `CNPJ: ${hideCnpj(document ?? "")}`;
      }
      if (data?.CountryCode) {
        return `${t("offline_recharge.pay_recharge.cpf_label")}: ${document} (${data.CountryCode})`;
      }
      return `${t("offline_recharge.pay_recharge.cpf_label")}: ${document}`;
    },
    [t]
  );


  return {
    step,
    offlineCard,
    rechargeValueCents,
    activationDisplayValue,
    showRetryReadButton,
    currentStepIndex,
    handleGoBack,
    onRetryRead,
    onSubmitCpf,
    onSubmitRechargeValue,
    addQuickValue,
    handleKeyPress,
    onClearValue,
    getDocumentDisplay,
    resetFlow,
  };
}

